/// <amd-module name="VSS/Diagnostics/Dialog/FaultInjectionFaultInputs" />
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import * as FaultInjectionResources from "VSS/Diagnostics/Dialog/Resources";
import { Components, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { TextField } from "VSSUI/TextField";

export interface IFaultInjectionFaultInputsProps {
    onFaultAdd: (faultItem: IFaultItem) => void;
}

interface IFaultInput {
    shouldShow: ObservableValue<boolean>;
    checkShouldShow(faultPoint: FaultPoint, faultType: FaultType): boolean;
    render(): JSX.Element;
    validate(input: string): boolean;
}

export class IFaultItem {
    public faultType: FaultType;
    public faultPoint: FaultPoint;
    public settings: object;
}

export enum FaultType {
    Delay = "Delay",
    Exception = "Exception"
}

export enum FaultPoint {
    REST = "REST"
}

export class FaultInjectionFaultInputs extends React.Component<IFaultInjectionFaultInputsProps> {
    private faultType = new ObservableValue<FaultType>(FaultType.Delay);
    private faultPoint = new ObservableValue<FaultPoint>(FaultPoint.REST);

    private faultTarget = new ObservableValue<string>("");
    private faultDelay = new ObservableValue<string>("");

    private faultHelperBarItems: IHeaderCommandBarItem[] = [
        {
            iconProps: {
                iconName: "Add"
            },
            id: "faultListAdd",
            important: true,
            isPrimary: true,
            onActivate: () => {
                this.addToFaultProfile();
            },
            text: FaultInjectionResources.AddFaultListButtonText,
            tooltipProps: {
                text: FaultInjectionResources.AddFaultListButtonTipText
            }
        }
    ];

    private targetInput: IFaultInput = {
        shouldShow: new ObservableValue<boolean>(false),
        checkShouldShow: (faultPoint: FaultPoint, faultType: FaultType): boolean => {
            return true;
        },
        render: (): JSX.Element => {
            return (
                <Observer faultTarget={this.faultTarget}>
                    {(props: { faultTarget: string }) => {
                        return (
                            <FormItem
                                className="fault-settings-input fault-settings-input-target flex-grow"
                                label={FaultInjectionResources.FaultInputTargetHostLabel}
                                message={
                                    this.targetInput.validate(props.faultTarget) ? "" : FaultInjectionResources.FaultInputTargetHostValidationText
                                }
                                error={!this.targetInput.validate(props.faultTarget)}
                            >
                                <TextField
                                    value={this.faultTarget}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>, newValue: string) => (this.faultTarget.value = newValue)}
                                    readOnly={false}
                                    placeholder={FaultInjectionResources.FaultInputTargetHostPlaceHolder}
                                    className="fault-settings-input-textfield"
                                />
                            </FormItem>
                        );
                    }}
                </Observer>
            );
        },
        validate: (input: string): boolean => {
            if (input) {
                return true;
            } else {
                return false;
            }
        }
    };

    private delayInput: IFaultInput = {
        shouldShow: new ObservableValue<boolean>(false),
        checkShouldShow: (faultPoint: FaultPoint, faultType: FaultType): boolean => {
            return faultType == FaultType.Delay;
        },
        render: (): JSX.Element => {
            return (
                <Observer faultDelay={this.faultDelay}>
                    {(props: { faultDelay: string }) => {
                        return (
                            <FormItem
                                className="fault-settings-input fault-settings-input-delay"
                                label={FaultInjectionResources.FaultInputDelayLabel}
                                message={this.delayInput.validate(props.faultDelay) ? "" : FaultInjectionResources.FaultInputDelayValidationText}
                                error={!this.delayInput.validate(props.faultDelay)}
                            >
                                <TextField
                                    value={this.faultDelay}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>, newValue: string) => {
                                        this.faultDelay.value = newValue;
                                    }}
                                    readOnly={false}
                                    placeholder={FaultInjectionResources.FaultInputDelayPlaceHolder}
                                    className="fault-settings-input-textfield"
                                />
                            </FormItem>
                        );
                    }}
                </Observer>
            );
        },
        validate: (input: string): boolean => {
            if (input && !isNaN(Number(input))) {
                return true;
            } else {
                return false;
            }
        }
    };

    public render(): JSX.Element {
        this.refreshSettingInputs();
        return (
            <React.Fragment>
                <Card
                    titleProps={{ text: FaultInjectionResources.FaultInjectionAddFaultCardTitle }}
                    headerCommandBarItems={this.faultHelperBarItems}
                    className="flex-grow"
                >
                    <div className="flex-row flex-grow">
                        <div className="flex-column fault-settings-input">
                            <FormItem className="fault-dropdown flex-grow" label={FaultInjectionResources.FaultInputFaultPointLabel}>
                                <Dropdown
                                    items={Object.keys(FaultPoint)}
                                    placeholder={FaultPoint.REST}
                                    onSelect={this.onFaultPointSelect}
                                    className="fault-dropdown-picker"
                                />
                            </FormItem>
                        </div>
                        <div className="flex-column fault-settings-input">
                            <FormItem className="fault-dropdown flex-grow" label={FaultInjectionResources.FaultInputFaultTypeLabel}>
                                <Dropdown
                                    items={Object.keys(FaultType)}
                                    placeholder={FaultType.Delay}
                                    onSelect={this.onFaultTypeSelect}
                                    className="fault-dropdown-picker"
                                />
                            </FormItem>
                        </div>
                        {this.renderSettingInputs()}
                    </div>
                </Card>
            </React.Fragment>
        );
    }

    private onFaultTypeSelect = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<FaultType>) => {
        this.faultType.value = item.text as FaultType;
        this.refreshSettingInputs();
    };

    private onFaultPointSelect = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<FaultPoint>) => {
        this.faultPoint.value = item.text as FaultPoint;
        this.refreshSettingInputs();
    };

    // register behavior for new fault input in all below functions
    private refreshSettingInputs(): void {
        this.targetInput.shouldShow.value = this.targetInput.checkShouldShow(this.faultPoint.value, this.faultType.value);
        this.delayInput.shouldShow.value = this.delayInput.checkShouldShow(this.faultPoint.value, this.faultType.value);
    }

    private renderSettingInputs(): JSX.Element {
        return (
            <div className="flex-row flex-grow">
                <ConditionalChildren renderChildren={this.targetInput.shouldShow}>{this.targetInput.render()}</ConditionalChildren>
                <ConditionalChildren renderChildren={this.delayInput.shouldShow}>{this.delayInput.render()}</ConditionalChildren>
            </div>
        );
    }

    private addToFaultProfile(): void {
        let newFaultToAdd = new IFaultItem();
        newFaultToAdd.faultPoint = this.faultPoint.value;
        newFaultToAdd.faultType = this.faultType.value;
        let settings: any = {};

        // validate fields and add fields to fault settings
        if (this.targetInput.checkShouldShow(this.faultPoint.value, this.faultType.value)) {
            if (this.targetInput.validate(this.faultTarget.value)) {
                settings.target = this.faultTarget.value;
            } else {
                return;
            }
        }
        if (this.delayInput.checkShouldShow(this.faultPoint.value, this.faultType.value)) {
            if (this.delayInput.validate(this.faultDelay.value)) {
                settings.delayMs = this.faultDelay.value;
            } else {
                return;
            }
        }

        newFaultToAdd.settings = settings;
        this.props.onFaultAdd(newFaultToAdd);
    }
}
